import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { User } from "../types/user";

export interface AuthResponse {
  token?: string;
  user?: User;
  error?: string;
  message?: string;
  requiresConfirmation?: boolean;
}

export interface RegisterRequest {
  email: string;
  password: string;
  tenantSlug?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
}

export interface ConfirmEmailRequest {
  token: string;
  password?: string;
}

export interface ValidateTokenRequest {
  token: string;
}

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_BASE_URL,
});

export const authApiSlice = createApi({
  reducerPath: "authApiSlice",
  baseQuery,
  tagTypes: ["Auth"],
  endpoints: (builder) => ({
    register: builder.mutation<AuthResponse, RegisterRequest>({
      query: (credentials) => ({
        url: "/auth/register",
        method: "POST",
        body: credentials,
      }),
    }),

    login: builder.mutation<AuthResponse, LoginRequest>({
      query: (credentials) => ({
        url: "/auth/login",
        method: "POST",
        body: credentials,
      }),
    }),

    requestPasswordReset: builder.mutation<
      { message: string },
      { email: string }
    >({
      query: ({ email }) => ({
        url: "/auth/request-password-reset",
        method: "POST",
        body: { email },
      }),
    }),

    resetPassword: builder.mutation<{ message: string }, ResetPasswordRequest>({
      query: (resetData) => ({
        url: "/auth/reset-password",
        method: "POST",
        body: resetData,
      }),
    }),

    validateToken: builder.mutation<{ valid: boolean }, ValidateTokenRequest>({
      query: ({ token }) => ({
        url: "/auth/validate-token",
        method: "POST",
        body: { token },
      }),
    }),

    confirmEmail: builder.mutation<AuthResponse, ConfirmEmailRequest>({
      query: (confirmData) => ({
        url: "/auth/confirm-email",
        method: "POST",
        body: confirmData,
      }),
    }),
  }),
});

export const {
  useRegisterMutation,
  useLoginMutation,
  useRequestPasswordResetMutation,
  useResetPasswordMutation,
  useValidateTokenMutation,
  useConfirmEmailMutation,
} = authApiSlice;
